<template>
    <section class="search flow__panel flow__container-fluid">
        <div class="search__main">
            <h2 class="search__title">
                {{ myResources.title }}
            </h2>
            <p class="search__text">
                {{ myResources.introText }}
            </p>
            <hh-form class="search__form">
                <hh-error-display v-if="showPostcodeValidationWarning" class="search__postcode-msg">
                    {{ myResources.postcodeValidationError }}
                </hh-error-display>
                <hh-error-display v-else-if="showPostcodeRelevantWarning" class="search__postcode-msg">
                    {{ myResources.postcodeSearchError }}
                </hh-error-display>
                <hh-textbox def="postcode"
                            class="search__form__postcode"
                            :placeholder="myResources.placeholderText"
                            v-model="postcode"
                            @keyup.enter="onSearchClick"
                            @blur="validatePostcode" />
                <hh-form-group>
                    <hh-label for-def="terms"
                              :tabstop="true">
                        <hh-checkbox def="terms"
                                     class="search__form__terms-box"
                                     v-model="termsAccepted"
                                     :tabstop="false" />I agree to {{ this.scheme.title }}'s <a class="registration__form__link" href="#" @click="onTermsClick">Terms &amp; Conditions</a>.
                    </hh-label>
                </hh-form-group>
                <hh-btn type="cta"
                        class="search__form__next"
                        :disabled="cannotProceed"
                        :text="commonResources.continue"
                        track-operation-state
                        :operation-in-progress="isSearching"
                        @click="onSearchClick" />
                <hh-form-group v-if="showRecaptcha">
                    <hh-recaptcha-invisible ref="invisibleRecaptcha"
                                            class="search__form__catch"
                                            @verified="onVerified"
                                            @already-verified="onAlreadyVerified" />
                </hh-form-group>
            </hh-form>
        </div>
    </section>
</template>

<script>

import Vuex from 'vuex'

import AuthManager from '@/auth/auth-manager'
import PropertyManager from '@/property-manager'
import ProgressManager from '@/progress-manager'

import AnalyticsPage from './analytics-page'

import PolicyPresenter from '@/components/copy/policy-presenter'
import TermsAndConditions from '@/components/copy/terms'
import ResourcesHelper from '@/components/resources-helper'

export default {
    mixins: [
        AnalyticsPage,
        PolicyPresenter,
        ResourcesHelper,
    ],

    data() {
        return {
            postcode: '',
            showRecaptcha: true,
            captchaVerified: false,
            relevantPostcode: true,
            termsAccepted: false,
            isSearching: false,
            postcodeValid: true,
        }
    },

    computed: {
        ...Vuex.mapGetters([
            'guestSignedIn',
            'userSignedIn',
        ]),

        /**
         * @returns {boolean} A flag to indicate if the user cannot proceed.
         */
        cannotProceed() {
            return !this.userSignedIn && !(this.postcodeValid && this.postcode !== '') || !this.termsAccepted
        },

        /**
         * Return the resources object for this page.
         * @return {SearchResources}
         */
        myResources() {
            return this.parseResources('views.search')
        },

        /**
         * Return true if the postcode warning should be displayed.
         * @return {boolean}
         */
        showPostcodeValidationWarning() {
            return this.postcode !== '' && !this.postcodeValid
        },

        /**
         * Return true if the server validation fails.
         * @return {boolean}
         */
        showPostcodeRelevantWarning() {
            return this.postcode !== '' && !this.relevantPostcode
        },
    },

    watch: {
        postcode() {
            this.relevantPostcode = true
        },
    },

    mounted() {
        if (this.userSignedIn) this.showRecaptcha = false
    },

    methods: {
        /**
         * Validate the current postcode for correct UK format and store in local state.
         */
        validatePostcode() {
            this.postcodeValid = /^([A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2}|GIR ?0A)$/i.test(this.postcode)
        },

        /**
         * Search for the existance of a postcode. Called internally once recaptcha verification is complete.
         */
        async _executeSearch() {
            if (this.postcode && this.postcodeValid) {
                this.isSearching = true
                this.trackEvent('address_search', 'postcode_searched')
                this.trackEvent('address_search', 'postcode_search', { additional: this.postcode, label: null, value: this.postcode })
                this.relevantPostcode = await PropertyManager.isPostcodeRelevantAsync(this.postcode)
                this.isSearching = false

                if (this.relevantPostcode)
                    this.$router.push(ProgressManager.getNext(this.$route.name, { postcode: this.postcode }))
            }
        },

        /**
         * Handle recaptcha already verified event.
         */
        onAlreadyVerified(verified) {
            this.showRecaptcha = !this.userSignedIn && !verified
        },

        /**
         * Handle terms and conditions click event.
         */
        onTermsClick() {
            this.showPolicy(TermsAndConditions, 'Terms & Conditions')
        },

        /**
         * Handle recaptcha verification event.
         */
        async onVerified(isVerified = true) {
            this.captchaVerified = isVerified

            if (isVerified)
                await this._executeSearch()
        },

        /**
         * Handle search button click event.
         */
        async onSearchClick() {
            this.$logger.logObject({ postcode: this.postcode, captchaVerified: this.captchaVerified, signedInUser: this.userSignedIn, validPostcode: this.postcodeValid }, `Searching for postcode: ${ this.postcode }`, 'View - Search')
            if (this.cannotProceed) return

            if (this.guestSignedIn)
                await AuthManager.logOutGuestAsync()

            if ((this.captchaVerified || this.userSignedIn) && this.postcode)
                await this._executeSearch()
            else if (this.$refs.invisibleRecaptcha && this.$refs.invisibleRecaptcha.execute)
                this.$refs.invisibleRecaptcha.execute()
            else
                await AuthManager.logOutAsync()
        },
    },
}

</script>

<style lang="scss">

// Ensure colours and other visual style elements are added to theme files, not placed in here.
.search {
    @include recaptcha-or-login;

    .form {
        margin-top: $wide-gutter;

        @include element('field') {
            @include modifier('postcode') {
                max-width: 302px;
                border: solid 1px;

                &:focus {
                    border: solid 1px;
                }
            }
        }

        @include element('group') {
            margin-top: $wide-gutter;
            margin-bottom: $wide-gutter;
        }
    }

    .button {
        float: none;
        width: 150px;
    }
}

</style>
